import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/layout/page-header";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MonthReportTable, monthIso, monthLabel } from "@/features/monthly-reports";

type Search = { month?: string };

/** The last twelve closed months, latest first. */
const MONTHS = Array.from({ length: 12 }, (_, i) => monthIso(-(i + 1)));

export const Route = createFileRoute("/_authenticated/admin/reports")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    month:
      typeof search.month === "string" && MONTHS.includes(search.month) ? search.month : undefined,
  }),
  component: MonthlyReportsPage,
});

/**
 * The figures behind every client's monthly report, one month at a time.
 * Computed by the platform and kept for good, so a report can be written
 * long after the LinkedIn data it came from has been removed on LinkedIn's
 * schedule.
 */
function MonthlyReportsPage() {
  const navigate = Route.useNavigate();
  const month = Route.useSearch().month ?? MONTHS[0] ?? monthIso(-1);

  return (
    <>
      <PageHeader
        eyebrow="The Motherhood"
        title="Monthly reports"
        description="Every client's figures for a month: the team's posting, their Page, and who tagged it."
        actions={
          <div className="flex items-center gap-2">
            <Label htmlFor="report-month" className="text-muted-foreground text-sm">
              Month
            </Label>
            <Select value={month} onValueChange={(m) => void navigate({ search: { month: m } })}>
              <SelectTrigger id="report-month" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {monthLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />
      <MonthReportTable month={month} />
    </>
  );
}
